import {
  SearchOverlaySnapshotSchema,
  type SearchOverlaySnapshot,
} from './types';

/**
 * sessionStorage adapter for the SearchOverlay's last completed search.
 * A snapshot is restored only while it is fresh (under 5 minutes old) and was
 * saved under the same access token + org id.
 */

const STORAGE_KEY = 'searchOverlay.lastState';
const MAX_AGE_MS = 5 * 60 * 1000;

/** sessionStorage, or null when unavailable (private mode, blocked storage). */
const getStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

export const clearSearchSnapshot = (): void => {
  getStorage()?.removeItem(STORAGE_KEY);
};

export const writeSearchSnapshot = (snapshot: SearchOverlaySnapshot): void => {
  try {
    getStorage()?.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage may be full or unavailable — persistence is best-effort.
  }
};

/** Returns the snapshot when fresh and same-scope; otherwise discards it. */
export const restoreSearchSnapshot = (
  accessToken: string,
  orgId: string
): SearchOverlaySnapshot | null => {
  const raw = getStorage()?.getItem(STORAGE_KEY);

  let snapshot: unknown;
  try {
    snapshot = raw ? JSON.parse(raw) : null;
  } catch {
    snapshot = null;
  }

  const { success, data: storedSnapshot } =
    SearchOverlaySnapshotSchema.safeParse(snapshot);

  if (
    success &&
    storedSnapshot.sessionToken === accessToken &&
    storedSnapshot.orgId === orgId &&
    Date.now() - storedSnapshot.savedAt < MAX_AGE_MS
  ) {
    return storedSnapshot;
  }

  clearSearchSnapshot();
  return null;
};
